import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

interface Employee {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  department: string;
  jobTitle: string;
  targetNetSalary?: number | null;
}

interface PayrollAdjustment {
  label: string;
  type: 'DEDUCTION' | 'ADDITION';
  amount: string;
}

interface PayrollRecord {
  id: string;
  employeeId: string;
  employee: Employee;
  month: number;
  year: number;
  workingDays: number;
  presentDays: number;
  leaveDays: number;
  absenceDays: number;
  baseSalary: number;
  travellingAllowance: number;
  compensation: number;
  travellingDeduction: number;
  totalDeductions: number;
  grossSalary: number;
  netSalary: number;
  status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'LOCKED';
  remarks?: string;
  submittedAt?: string;
  submittedBy?: string;
  rejectionReason?: string;
  rejectedBy?: string;
  adjustments?: { label: string; type: 'DEDUCTION' | 'ADDITION'; amount: number }[];
  compensations?: { id: string; label: string; amount: number }[];
  transfers?: { id: string; accountType: string; label: string; amount: number }[];
}

const Payroll: React.FC = () => {
  const { canProcessPayroll, canApprovePayroll, isAdmin, isSecretary } = useAuth();
  const [payrolls, setPayrolls] = useState<PayrollRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [processing, setProcessing] = useState(false);
  const [cycleDates, setCycleDates] = useState<{ startDate: string; endDate: string } | null>(null);

  // Edit modal (Treasurer)
  const [showModal, setShowModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState<PayrollRecord | null>(null);

  // View modal (Secretary — read-only)
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewPayroll, setViewPayroll] = useState<PayrollRecord | null>(null);

  // Reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingPayroll, setRejectingPayroll] = useState<PayrollRecord | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [editData, setEditData] = useState({
    baseSalary: '',
    travellingAllowance: '',
    remarks: '',
  });
  const [adjustments, setAdjustments] = useState<PayrollAdjustment[]>([]);
  const [editCompensations, setEditCompensations] = useState<{ label: string; amount: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [submittingAll, setSubmittingAll] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [lockingAll, setLockingAll] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchPayrolls = useCallback(async () => {
    try {
      setLoading(true);
      const [payrollRes, cycleRes] = await Promise.all([
        api.getPayrolls({ month: selectedMonth, year: selectedYear }),
        api.getPayrollCycle(selectedMonth, selectedYear),
      ]);
      if ((payrollRes as any).success) {
        setPayrolls((payrollRes as any).data || []);
      }
      if ((cycleRes as any).success) {
        const d = (cycleRes as any).data;
        setCycleDates({ startDate: d.startDate, endDate: d.endDate });
      }
    } catch (error) {
      console.error('Failed to fetch payrolls:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchPayrolls();
  }, [fetchPayrolls]);

  const handleProcessPayroll = async () => {
    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.processPayroll(selectedMonth, selectedYear);
      if ((response as any).success) {
        setSuccess(`Payroll processed for ${(response as any).data?.length || 0} employees`);
        fetchPayrolls();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to process payroll');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (id: string) => {
    try {
      setError('');
      const response = await api.submitPayroll(id);
      if ((response as any).success) {
        setSuccess('Payroll submitted for Secretary approval.');
        fetchPayrolls();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to submit payroll');
    }
  };

  const handleSubmitAll = async () => {
    const pending = payrolls.filter(p => p.status === 'DRAFT');
    if (pending.length === 0) {
      setError('No draft payrolls to submit');
      return;
    }
    if (!window.confirm(`Submit all ${pending.length} draft payrolls to the Secretary for approval?`)) return;

    setSubmittingAll(true);
    setError('');
    try {
      const response = await api.submitAllPayrolls(selectedMonth, selectedYear);
      if ((response as any).success) {
        setSuccess(`${pending.length} payrolls submitted for Secretary approval.`);
        fetchPayrolls();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to submit payrolls');
      fetchPayrolls();
    } finally {
      setSubmittingAll(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      setError('');
      const response = await api.approvePayroll(id);
      if ((response as any).success) {
        setSuccess('Payroll approved successfully');
        fetchPayrolls();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to approve payroll');
    }
  };

  const handleLock = async (id: string) => {
    try {
      setError('');
      const response = await api.lockPayroll(id);
      if ((response as any).success) {
        setSuccess('Payroll locked successfully');
        fetchPayrolls();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to lock payroll');
    }
  };

  const handleResetPayroll = async () => {
    const monthLabel = months.find(m => m.value === selectedMonth)?.label;
    const first = window.confirm(
      `WARNING: This will permanently delete all payroll records for ${monthLabel} ${selectedYear}, including any approved or locked records.\n\nAll payslips for this period will also be deleted.\n\nDo you want to proceed?`
    );
    if (!first) return;

    const second = window.confirm(
      `FINAL CONFIRMATION\n\nYou are about to reset payroll for ${monthLabel} ${selectedYear}. This action cannot be undone.\n\nAfter reset, payroll will need to be reprocessed from scratch.\n\nAre you absolutely sure?`
    );
    if (!second) return;

    try {
      setError('');
      const response = await api.resetPayroll(selectedMonth, selectedYear);
      if ((response as any).success) {
        setSuccess(`Payroll for ${monthLabel} ${selectedYear} has been reset. You can now reprocess it.`);
        fetchPayrolls();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to reset payroll');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this payroll record?')) return;

    try {
      setError('');
      const response = await api.deletePayroll(id);
      if ((response as any).success) {
        setSuccess('Payroll deleted successfully');
        fetchPayrolls();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete payroll');
    }
  };

  const handleReprocess = async (id: string) => {
    if (!window.confirm('Reprocess this payroll? This will refresh attendance data and reset it to DRAFT for secretary re-review.')) return;
    setError('');
    try {
      const response = await api.reprocessPayroll(id);
      if ((response as any).success) {
        setSuccess('Payroll reprocessed and reset to DRAFT for re-review.');
        fetchPayrolls();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to reprocess payroll');
    }
  };

  const handleApproveAll = async () => {
    const pending = payrolls.filter(p => p.status === 'SUBMITTED');
    if (pending.length === 0) {
      setError('No submitted payrolls to approve');
      return;
    }

    if (!window.confirm(`Approve all ${pending.length} submitted payrolls?`)) return;

    setApprovingAll(true);
    setError('');
    try {
      for (const payroll of pending) {
        await api.approvePayroll(payroll.id);
      }
      setSuccess(`${pending.length} payrolls approved`);
      fetchPayrolls();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to approve payrolls');
      fetchPayrolls();
    } finally {
      setApprovingAll(false);
    }
  };

  const handleLockAll = async () => {
    const approvedPayrolls = payrolls.filter(p => p.status === 'APPROVED');
    if (approvedPayrolls.length === 0) {
      setError('No approved payrolls to lock');
      return;
    }

    if (!window.confirm(`Lock all ${approvedPayrolls.length} approved payrolls?`)) return;

    setLockingAll(true);
    setError('');
    try {
      for (const payroll of approvedPayrolls) {
        await api.lockPayroll(payroll.id);
      }
      setSuccess(`${approvedPayrolls.length} payrolls locked`);
      fetchPayrolls();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to lock payrolls');
      fetchPayrolls();
    } finally {
      setLockingAll(false);
    }
  };

  const openRejectModal = (payroll: PayrollRecord) => {
    setRejectingPayroll(payroll);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejectingPayroll || !rejectionReason.trim()) return;
    setRejecting(true);
    setError('');
    try {
      const response = await api.rejectPayroll(rejectingPayroll.id, rejectionReason.trim());
      if ((response as any).success) {
        setSuccess(`Payroll rejected for ${rejectingPayroll.employee.firstName} ${rejectingPayroll.employee.lastName}. Treasurer has been notified.`);
        setShowRejectModal(false);
        fetchPayrolls();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to reject payroll');
    } finally {
      setRejecting(false);
    }
  };

  const STATUTORY_LABELS = ['CSG', 'NSF'];

  const openEditModal = (payroll: PayrollRecord) => {
    setEditError('');
    setSelectedPayroll(payroll);
    setEditData({
      baseSalary: payroll.baseSalary.toString(),
      travellingAllowance: payroll.travellingAllowance.toString(),
      remarks: payroll.remarks || '',
    });
    // Exclude statutory adjustments — they are auto-recalculated server-side
    setAdjustments(
      (payroll.adjustments || [])
        .filter(a => !STATUTORY_LABELS.includes(a.label))
        .map(a => ({ label: a.label, type: a.type, amount: a.amount.toString() }))
    );
    setEditCompensations(
      (payroll.compensations || []).map(c => ({ label: c.label, amount: Number(c.amount).toString() }))
    );
    setShowModal(true);
  };

  const addAdjustmentRow = () => {
    setAdjustments(prev => [...prev, { label: '', type: 'DEDUCTION', amount: '' }]);
  };

  const removeAdjustmentRow = (idx: number) => {
    setAdjustments(prev => prev.filter((_, i) => i !== idx));
  };

  const updateAdjustmentRow = (idx: number, field: keyof PayrollAdjustment, value: string) => {
    setAdjustments(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPayroll) return;

    const targetNetSalary = selectedPayroll.employee.targetNetSalary;
    if (targetNetSalary !== null && targetNetSalary !== undefined) {
      const liveBase = parseFloat(editData.baseSalary) || 0;
      const liveTA = parseFloat(editData.travellingAllowance) || 0;
      const liveComps = editCompensations.reduce((sum, compensation) => sum + (parseFloat(compensation.amount) || 0), 0);
      const liveAdditions = adjustments.filter(adjustment => adjustment.type === 'ADDITION').reduce((sum, adjustment) => sum + (parseFloat(adjustment.amount) || 0), 0);
      const liveUserDeductions = adjustments.filter(adjustment => adjustment.type === 'DEDUCTION').reduce((sum, adjustment) => sum + (parseFloat(adjustment.amount) || 0), 0);
      const liveCSG = liveBase <= 50000 ? liveBase * 0.015 : liveBase * 0.03;
      const liveNSF = liveBase >= 29710 ? 29710 * 0.01 : liveBase * 0.01;
      const liveTravelDeduction = Number(selectedPayroll.travellingDeduction) > 0
        ? (liveTA / Number(selectedPayroll.travellingAllowance || 1)) * Number(selectedPayroll.travellingDeduction)
        : 0;
      const manualTransfers = (selectedPayroll.transfers || [])
        .filter(transfer => transfer.label !== 'HSA (Target Net)')
        .reduce((sum, transfer) => sum + Number(transfer.amount), 0);
      const availableHsaTargetNet = Math.max(0, liveBase + liveTA + liveComps + liveAdditions - liveTravelDeduction - liveCSG - liveNSF - manualTransfers - Number(targetNetSalary));

      if (liveUserDeductions > availableHsaTargetNet) {
        setError('Deduction cannot be more than the amount of HSA Target Net');
        return;
      }
    }

    setSaving(true);
    setEditError('');
    setError('');

    try {
      const response = await api.updatePayroll(selectedPayroll.id, { ...editData, adjustments, compensations: editCompensations.map(c => ({ label: c.label, amount: parseFloat(c.amount) || 0 })) });
      if ((response as any).success) {
        setSuccess('Payroll updated successfully');
        setShowModal(false);
        fetchPayrolls();
      }
    } catch (error: any) {
      setEditError(error.response?.data?.message || 'Failed to update payroll');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      DRAFT: 'bg-yellow-100 text-yellow-800',
      SUBMITTED: 'bg-indigo-100 text-indigo-800',
      APPROVED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800',
      LOCKED: 'bg-gray-100 text-gray-800',
    };
    return `px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MU', {
      style: 'currency',
      currency: 'MUR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Employer contribution helpers
  const calcEmployerCSG = (base: number) => base <= 50000 ? base * 0.03 : base * 0.06;
  const calcEmployerNSF = (base: number) => Math.min(base, 29710) * 0.025;
  const calcTrainingLevy = (base: number) => base * 0.015;

  // Summary calculations
  const totalGross = payrolls.reduce((sum, p) => sum + Number(p.grossSalary), 0);
  const totalDeductions = payrolls.reduce((sum, p) => sum + Number(p.totalDeductions), 0);
  const totalNet = payrolls.reduce((sum, p) => sum + Number(p.netSalary), 0);
  const totalEmployerCSG = payrolls.reduce((sum, p) => sum + calcEmployerCSG(Number(p.baseSalary)), 0);
  const totalEmployerNSF = payrolls.reduce((sum, p) => sum + calcEmployerNSF(Number(p.baseSalary)), 0);
  const totalTrainingLevy = payrolls.reduce((sum, p) => sum + calcTrainingLevy(Number(p.baseSalary)), 0);
  const draftCount = payrolls.filter(p => p.status === 'DRAFT').length;
  const submittedCount = payrolls.filter(p => p.status === 'SUBMITTED').length;
  const approvedCount = payrolls.filter(p => p.status === 'APPROVED').length;
  const rejectedCount = payrolls.filter(p => p.status === 'REJECTED').length;
  const lockedCount = payrolls.filter(p => p.status === 'LOCKED').length;

  return (
    <Layout>
      <div className="px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Payroll Management</h1>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-md">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Secretary: notification banner when submitted payrolls are pending review */}
        {canApprovePayroll && !canProcessPayroll && submittedCount > 0 && (
          <div className="mb-4 p-4 bg-indigo-50 border border-indigo-300 rounded-md flex items-center gap-3">
            <svg className="w-5 h-5 text-indigo-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-indigo-800">
                {submittedCount} payroll{submittedCount !== 1 ? 's' : ''} submitted for your approval
              </p>
              <p className="text-xs text-indigo-600">Review the submitted payrolls below and approve or reject each one.</p>
            </div>
          </div>
        )}

        {/* Month/Year Selection & Process */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow">
          {cycleDates && (
            <p className="text-sm text-gray-500 mb-3">
              Attendance period: <span className="font-medium text-gray-700">{cycleDates.startDate}</span> to <span className="font-medium text-gray-700">{cycleDates.endDate}</span>
            </p>
          )}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 border p-2"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 border p-2"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            {canProcessPayroll && payrolls.length === 0 && (
              <button
                onClick={handleProcessPayroll}
                disabled={processing}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Process Payroll'}
              </button>
            )}
            {canProcessPayroll && payrolls.length > 0 && payrolls.every(p => p.status === 'DRAFT') && (
              <button
                onClick={() => {
                  if (!window.confirm('Re-processing will delete all current DRAFT payrolls for this month and recalculate from scratch. Continue?')) return;
                  handleProcessPayroll();
                }}
                disabled={processing}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Re-process Payroll'}
              </button>
            )}
            {canProcessPayroll && payrolls.length > 0 && draftCount > 0 && (
              <button
                onClick={handleSubmitAll}
                disabled={submittingAll}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submittingAll && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {submittingAll ? 'Submitting...' : `Submit All for Approval (${draftCount})`}
              </button>
            )}
            {canApprovePayroll && payrolls.length > 0 && submittedCount > 0 && (
              <button
                onClick={handleApproveAll}
                disabled={approvingAll}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {approvingAll && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {approvingAll ? 'Approving...' : `Approve All (${submittedCount})`}
              </button>
            )}
            {canProcessPayroll && payrolls.length > 0 && approvedCount > 0 && (
              <button
                onClick={handleLockAll}
                disabled={lockingAll}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
              >
                {lockingAll && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                )}
                {lockingAll ? 'Locking...' : `Lock All (${approvedCount})`}
              </button>
            )}
            {isAdmin && payrolls.length > 0 && (
              <button
                onClick={handleResetPayroll}
                className="px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 ml-auto"
              >
                Reset Payroll
              </button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        {payrolls.length > 0 && (
          <div className="mb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-white p-3 rounded-lg shadow">
              <div className="text-xs text-gray-500">Employees</div>
              <div className="text-xl font-bold text-gray-900">{payrolls.length}</div>
            </div>
            <div className="bg-white p-3 rounded-lg shadow min-w-0">
              <div className="text-xs text-gray-500">Total Gross</div>
              <div className="text-base font-bold text-gray-900 truncate">{formatCurrency(totalGross)}</div>
            </div>
            <div className="bg-white p-3 rounded-lg shadow min-w-0">
              <div className="text-xs text-gray-500">Total Deductions</div>
              <div className="text-base font-bold text-red-600 truncate">{formatCurrency(totalDeductions)}</div>
            </div>
            <div className="bg-white p-3 rounded-lg shadow min-w-0">
              <div className="text-xs text-gray-500">Total Net</div>
              <div className="text-base font-bold text-green-600 truncate">{formatCurrency(totalNet)}</div>
            </div>
            <div className="bg-white p-3 rounded-lg shadow">
              <div className="text-xs text-gray-500">Status</div>
              <div className="flex gap-1 mt-1 flex-wrap">
                {draftCount > 0 && <span className={getStatusBadge('DRAFT')}>{draftCount} Draft</span>}
                {submittedCount > 0 && <span className={getStatusBadge('SUBMITTED')}>{submittedCount} Submitted</span>}
                {approvedCount > 0 && <span className={getStatusBadge('APPROVED')}>{approvedCount} Approved</span>}
                {rejectedCount > 0 && <span className={getStatusBadge('REJECTED')}>{rejectedCount} Rejected</span>}
                {lockedCount > 0 && <span className={getStatusBadge('LOCKED')}>{lockedCount} Locked</span>}
              </div>
            </div>
            {canProcessPayroll && (
              <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg shadow col-span-2 sm:col-span-1">
                <div className="text-xs text-orange-700 font-medium mb-1">Employer Contributions</div>
                <div className="text-xs text-orange-600 flex justify-between"><span>CSG:</span><span className="font-semibold">{formatCurrency(totalEmployerCSG)}</span></div>
                <div className="text-xs text-orange-600 flex justify-between"><span>NSF:</span><span className="font-semibold">{formatCurrency(totalEmployerNSF)}</span></div>
                <div className="text-xs text-orange-600 flex justify-between"><span>Training Levy:</span><span className="font-semibold">{formatCurrency(totalTrainingLevy)}</span></div>
                <div className="text-xs text-orange-800 font-bold flex justify-between border-t border-orange-200 mt-1 pt-1"><span>Total:</span><span>{formatCurrency(totalEmployerCSG + totalEmployerNSF + totalTrainingLevy)}</span></div>
              </div>
            )}
          </div>
        )}

        {/* Payroll Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : payrolls.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
            <p>No payroll records for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}.</p>
            {canProcessPayroll && (
              <p className="mt-2 text-sm">Click "Process Payroll" to generate payroll for all active employees.</p>
            )}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Base Salary</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Allowances</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deductions</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Salary</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    {(canProcessPayroll || canApprovePayroll) && (
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payrolls.map((payroll) => (
                    <tr key={payroll.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {payroll.employee.firstName} {payroll.employee.lastName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {payroll.employee.employeeId} &bull; {payroll.employee.department}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-xs text-gray-600">
                          <div>Working: {payroll.workingDays}</div>
                          <div className="text-green-600">Present: {payroll.presentDays}</div>
                          <div className="text-blue-600">Leave: {payroll.leaveDays}</div>
                          {payroll.absenceDays > 0 && (
                            <div className="text-red-600">Absent: {payroll.absenceDays}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                        {formatCurrency(Number(payroll.baseSalary))}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900">
                          {formatCurrency(Number(payroll.travellingAllowance))}
                        </div>
                        <div className="text-xs text-gray-500">
                          Travel: {formatCurrency(Number(payroll.travellingAllowance))}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="text-sm text-red-600">
                          -{formatCurrency(Number(payroll.totalDeductions))}
                        </div>
                        {Number(payroll.travellingDeduction) > 0 && (
                          <div className="text-xs text-gray-500">Travel: {formatCurrency(Number(payroll.travellingDeduction))}</div>
                        )}
                        {(payroll.adjustments || []).filter(a => a.type === 'DEDUCTION').map((a, i) => (
                          <div key={i} className="text-xs text-gray-500">{a.label}: {formatCurrency(Number(a.amount))}</div>
                        ))}
                        {(payroll.adjustments || []).filter(a => a.type === 'ADDITION').length > 0 && (
                          <div className="text-xs text-green-600">
                            +{formatCurrency((payroll.adjustments || []).filter(a => a.type === 'ADDITION').reduce((s, a) => s + Number(a.amount), 0))} additions
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-green-700">
                        {formatCurrency(Number(payroll.netSalary))}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={getStatusBadge(payroll.status)}>{payroll.status}</span>
                        {payroll.status === 'REJECTED' && payroll.rejectionReason && (
                          <p className="text-xs text-red-600 mt-1 max-w-xs mx-auto" title={payroll.rejectionReason}>
                            {payroll.rejectionReason.length > 60 ? payroll.rejectionReason.slice(0, 60) + '…' : payroll.rejectionReason}
                          </p>
                        )}
                      </td>
                      {(canProcessPayroll || canApprovePayroll) && (
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            {/* Secretary: View button for any status */}
                            {canApprovePayroll && !canProcessPayroll && (
                              <button
                                onClick={() => { setViewPayroll(payroll); setShowViewModal(true); }}
                                className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                              >
                                View
                              </button>
                            )}
                            {/* Treasurer: Edit (not on locked) */}
                            {canProcessPayroll && payroll.status !== 'LOCKED' && (
                              <button
                                onClick={() => openEditModal(payroll)}
                                className="text-primary-600 hover:text-primary-900 text-sm"
                              >
                                Edit
                              </button>
                            )}
                            {/* Treasurer: View-only for locked */}
                            {canProcessPayroll && payroll.status === 'LOCKED' && (
                              <button
                                onClick={() => { setViewPayroll(payroll); setShowViewModal(true); }}
                                className="text-gray-500 hover:text-gray-700 text-sm"
                              >
                                View
                              </button>
                            )}
                            {/* Treasurer: Submit for Approval on DRAFT */}
                            {canProcessPayroll && payroll.status === 'DRAFT' && (
                              <button
                                onClick={() => handleSubmit(payroll.id)}
                                className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                              >
                                Submit
                              </button>
                            )}
                            {/* Secretary: Approve / Reject only on SUBMITTED */}
                            {canApprovePayroll && !canProcessPayroll && payroll.status === 'SUBMITTED' && (
                              <>
                                <button
                                  onClick={() => handleApprove(payroll.id)}
                                  className="text-green-600 hover:text-green-900 text-sm font-medium"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => openRejectModal(payroll)}
                                  className="text-red-600 hover:text-red-900 text-sm font-medium"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {/* Admin can also approve/reject on SUBMITTED */}
                            {canApprovePayroll && canProcessPayroll && payroll.status === 'SUBMITTED' && (
                              <>
                                <button
                                  onClick={() => handleApprove(payroll.id)}
                                  className="text-green-600 hover:text-green-900 text-sm font-medium"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => openRejectModal(payroll)}
                                  className="text-red-600 hover:text-red-900 text-sm font-medium"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {/* Treasurer: Reprocess button for REJECTED */}
                            {canProcessPayroll && payroll.status === 'REJECTED' && (
                              <button
                                onClick={() => handleReprocess(payroll.id)}
                                className="text-orange-600 hover:text-orange-900 text-sm font-medium"
                              >
                                Reprocess
                              </button>
                            )}
                            {/* Treasurer: Lock on APPROVED */}
                            {canProcessPayroll && payroll.status === 'APPROVED' && (
                              <button
                                onClick={() => handleLock(payroll.id)}
                                className="text-gray-600 hover:text-gray-900 text-sm"
                              >
                                Lock
                              </button>
                            )}
                            {/* Treasurer: Delete (not on locked) */}
                            {canProcessPayroll && payroll.status !== 'LOCKED' && (
                              <button
                                onClick={() => handleDelete(payroll.id)}
                                className="text-red-600 hover:text-red-900 text-sm"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Secretary View Modal (read-only) */}
        {showViewModal && viewPayroll && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Payroll Details — {viewPayroll.employee.firstName} {viewPayroll.employee.lastName}
                  </h2>
                  <p className="text-sm text-gray-500">{viewPayroll.employee.employeeId} · {viewPayroll.employee.department}</p>
                </div>
                <button onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              {/* Status */}
              <div className="mb-4">
                <span className={getStatusBadge(viewPayroll.status)}>{viewPayroll.status}</span>
                {viewPayroll.status === 'REJECTED' && viewPayroll.rejectionReason && (
                  <p className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                    <span className="font-semibold">Rejection reason:</span> {viewPayroll.rejectionReason}
                  </p>
                )}
              </div>

              {/* Attendance */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md text-sm text-gray-600 grid grid-cols-2 gap-x-6 gap-y-1">
                <div className="flex justify-between"><span>Working Days:</span><span className="font-medium">{viewPayroll.workingDays}</span></div>
                <div className="flex justify-between"><span>Present Days:</span><span className="font-medium text-green-600">{viewPayroll.presentDays}</span></div>
                <div className="flex justify-between"><span>Leave Days:</span><span className="font-medium text-blue-600">{viewPayroll.leaveDays}</span></div>
                <div className="flex justify-between"><span>Absence Days:</span><span className="font-medium text-red-600">{viewPayroll.absenceDays}</span></div>
              </div>

              {/* Salary breakdown */}
              <div className="mb-4 space-y-2 text-sm">
                <h3 className="font-semibold text-gray-700 mb-1">Earnings</h3>
                <div className="flex justify-between px-2"><span>Base Salary</span><span>{formatCurrency(Number(viewPayroll.baseSalary))}</span></div>
                {Number(viewPayroll.travellingAllowance) > 0 && (
                  <div className="flex justify-between px-2"><span>Travelling Allowance</span><span>{formatCurrency(Number(viewPayroll.travellingAllowance))}</span></div>
                )}
                {(viewPayroll.compensations || []).map((c, i) => (
                  <div key={i} className="flex justify-between px-2 text-amber-700"><span>{c.label}</span><span>{formatCurrency(Number(c.amount))}</span></div>
                ))}
                {(viewPayroll.adjustments || []).filter(a => a.type === 'ADDITION').map((a, i) => (
                  <div key={i} className="flex justify-between px-2 text-green-700"><span>{a.label}</span><span>+{formatCurrency(Number(a.amount))}</span></div>
                ))}
                <div className="flex justify-between px-2 font-semibold border-t pt-1">
                  <span>Gross Salary</span><span>{formatCurrency(Number(viewPayroll.grossSalary))}</span>
                </div>

                <h3 className="font-semibold text-gray-700 mt-3 mb-1">Deductions</h3>
                {Number(viewPayroll.travellingDeduction) > 0 && (
                  <div className="flex justify-between px-2 text-red-600"><span>Travel Deduction</span><span>-{formatCurrency(Number(viewPayroll.travellingDeduction))}</span></div>
                )}
                {(viewPayroll.adjustments || []).filter(a => a.type === 'DEDUCTION').map((a, i) => (
                  <div key={i} className="flex justify-between px-2 text-red-600"><span>{a.label}</span><span>-{formatCurrency(Number(a.amount))}</span></div>
                ))}
              </div>

              {/* Transfers */}
              {(viewPayroll.transfers || []).length > 0 && (
                <div className="mb-4 space-y-2 text-sm">
                  <h3 className="font-semibold text-gray-700 mb-1">Transfers</h3>
                  {(viewPayroll.transfers || []).map((t, i) => (
                    <div key={i} className="flex justify-between px-2 text-blue-700">
                      <span>{t.label}{(t as any).accountIdentifier ? ` — ${(t as any).accountIdentifier}` : ''}</span>
                      <span>{formatCurrency(Number(t.amount))}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mb-2 space-y-2 text-sm">
                <div className="flex justify-between px-2 font-semibold border-t pt-1 text-red-700">
                  <span>Total Deductions</span><span>-{formatCurrency(Number(viewPayroll.totalDeductions))}</span>
                </div>
              </div>

              {/* Net */}
              <div className="bg-gray-900 text-white rounded-md p-3 flex justify-between items-center mb-4">
                <span className="font-semibold">Net Salary</span>
                <span className="text-xl font-bold">{formatCurrency(Number(viewPayroll.netSalary))}</span>
              </div>

              {/* Employer Contributions (not part of employee net salary) */}
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md text-sm">
                <p className="text-xs font-semibold text-orange-700 mb-2 uppercase tracking-wide">Employer Contributions (not deducted from employee)</p>
                <div className="flex justify-between text-orange-800">
                  <span>Employer CSG ({Number(viewPayroll.baseSalary) <= 50000 ? '3%' : '6%'})</span>
                  <span>{formatCurrency(calcEmployerCSG(Number(viewPayroll.baseSalary)))}</span>
                </div>
                <div className="flex justify-between text-orange-800 mt-1">
                  <span>Employer NSF (2.5%)</span>
                  <span>{formatCurrency(calcEmployerNSF(Number(viewPayroll.baseSalary)))}</span>
                </div>
                <div className="flex justify-between text-orange-800 mt-1">
                  <span>Training Levy (1.5%)</span>
                  <span>{formatCurrency(calcTrainingLevy(Number(viewPayroll.baseSalary)))}</span>
                </div>
                <div className="flex justify-between text-orange-900 font-semibold mt-2 border-t border-orange-200 pt-2">
                  <span>Total Employer Contribution</span>
                  <span>{formatCurrency(calcEmployerCSG(Number(viewPayroll.baseSalary)) + calcEmployerNSF(Number(viewPayroll.baseSalary)) + calcTrainingLevy(Number(viewPayroll.baseSalary)))}</span>
                </div>
              </div>

              {/* Actions — only for SUBMITTED (Secretary can approve/reject) */}
              {viewPayroll.status === 'SUBMITTED' && (
                <div className="flex justify-end gap-3 pt-2 border-t">
                  <button
                    onClick={() => openRejectModal(viewPayroll)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
                  >
                    Reject
                  </button>
                  <button
                    onClick={async () => {
                      await handleApprove(viewPayroll.id);
                      setShowViewModal(false);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
                  >
                    Approve
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && rejectingPayroll && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Reject Payroll</h2>
              <p className="text-sm text-gray-500 mb-4">
                {rejectingPayroll.employee.firstName} {rejectingPayroll.employee.lastName} — {rejectingPayroll.employee.employeeId}
              </p>
              <form onSubmit={handleReject}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={4}
                    required
                    placeholder="Describe what needs to be corrected (e.g. incorrect base salary, missing allowance…)"
                    className="w-full rounded-md border-gray-300 border p-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">The Treasurer will be notified by email with this reason.</p>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRejectModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={rejecting || !rejectionReason.trim()}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {rejecting ? 'Rejecting…' : 'Reject Payroll'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showModal && selectedPayroll && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Edit Payroll — {selectedPayroll.employee.firstName} {selectedPayroll.employee.lastName}
              </h2>
              {editError && (
                <div role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {editError}
                </div>
              )}
              <form onSubmit={handleSaveEdit}>

                {/* Base amounts */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary</label>
                    <input type="number" step="0.01" value={editData.baseSalary}
                      onChange={(e) => setEditData({ ...editData, baseSalary: e.target.value })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 border p-2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Travelling Allowance</label>
                    <input type="number" step="0.01" value={editData.travellingAllowance}
                      onChange={(e) => setEditData({ ...editData, travellingAllowance: e.target.value })}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 border p-2" />
                  </div>
                </div>

                {/* Compensation lines — editable */}
                {editCompensations.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-800">Compensations</h3>
                    </div>
                    <div className="space-y-2">
                      {editCompensations.map((c, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <input
                            type="text"
                            value={c.label}
                            readOnly
                            className="flex-1 min-w-[120px] rounded-md border-gray-200 bg-gray-50 border p-2 text-sm text-gray-600 cursor-default"
                          />
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={c.amount}
                            onChange={(e) => setEditCompensations(prev => prev.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))}
                            className="w-28 rounded-md border-gray-300 border p-2 text-sm text-right"
                          />
                          <button
                            type="button"
                            onClick={() => setEditCompensations(prev => prev.filter((_, i) => i !== idx))}
                            className="text-red-400 hover:text-red-600 text-lg font-bold px-1"
                            title="Remove compensation"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transfer lines (read-only snapshot) */}
                {selectedPayroll.transfers && selectedPayroll.transfers.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-xs font-semibold text-blue-700 mb-2">Transfers (snapshot from processing)</p>
                    {selectedPayroll.transfers.map((t) => (
                      <div key={t.id} className="flex justify-between text-sm text-blue-800">
                        <span>{t.label}{(t as any).accountIdentifier ? ` — ${(t as any).accountIdentifier}` : ''}</span>
                        <span>{formatCurrency(Number(t.amount))}</span>
                      </div>
                    ))}
                    <p className="text-xs text-blue-600 mt-1">To change transfer amounts, edit them on the employee's profile and reprocess payroll.</p>
                  </div>
                )}

                {/* Attendance summary */}
                <div className="mb-4 p-3 bg-gray-50 rounded-md text-sm text-gray-600 grid grid-cols-2 gap-x-6 gap-y-1">
                  <div className="flex justify-between"><span>Working Days:</span><span>{selectedPayroll.workingDays}</span></div>
                  <div className="flex justify-between"><span>Present Days:</span><span className="text-green-600">{selectedPayroll.presentDays}</span></div>
                  <div className="flex justify-between"><span>Leave Days:</span><span className="text-blue-600">{selectedPayroll.leaveDays}</span></div>
                  <div className="flex justify-between"><span>Absence Days:</span><span className="text-red-600">{selectedPayroll.absenceDays}</span></div>
                </div>

                {/* Statutory deductions (read-only — recalculated server-side on save) */}
                {selectedPayroll.adjustments && selectedPayroll.adjustments.some(a => STATUTORY_LABELS.includes(a.label)) && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-xs font-semibold text-blue-700 mb-2">Statutory Deductions (auto-calculated)</p>
                    {selectedPayroll.adjustments.filter(a => STATUTORY_LABELS.includes(a.label)).map((a, i) => (
                      <div key={i} className="flex justify-between text-sm text-blue-800">
                        <span>{a.label}</span>
                        <span>{formatCurrency(Number(a.amount))}</span>
                      </div>
                    ))}
                    <p className="text-xs text-blue-600 mt-1">Recalculated automatically from Base Salary on each save.</p>
                  </div>
                )}

                {/* Adjustments */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-800">Additional Adjustments</h3>
                    <button type="button" onClick={addAdjustmentRow}
                      className="text-sm px-3 py-1 bg-primary-50 text-primary-700 border border-primary-300 rounded-md hover:bg-primary-100">
                      + Add Line
                    </button>
                  </div>

                  {editError && (
                    <div role="alert" aria-live="assertive" className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {editError}
                    </div>
                  )}

                  {adjustments.length === 0 && (
                    <p className="text-xs text-gray-400 italic">No additional adjustments. Click "Add Line" to add extra deductions or bonus additions.</p>
                  )}

                  <div className="space-y-2">
                    {adjustments.map((adj, idx) => (
                      <div key={idx} className="flex flex-wrap gap-2 items-center">
                        <input
                          type="text"
                          placeholder="Label (e.g. NPF)"
                          value={adj.label}
                          onChange={(e) => updateAdjustmentRow(idx, 'label', e.target.value)}
                          className="flex-1 min-w-[120px] rounded-md border-gray-300 border p-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                        <select
                          value={adj.type}
                          onChange={(e) => updateAdjustmentRow(idx, 'type', e.target.value)}
                          className="rounded-md border-gray-300 border p-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                        >
                          <option value="DEDUCTION">Deduction</option>
                          <option value="ADDITION">Addition</option>
                        </select>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Amount"
                          value={adj.amount}
                          onChange={(e) => updateAdjustmentRow(idx, 'amount', e.target.value)}
                          className="w-24 rounded-md border-gray-300 border p-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                        />
                        <button type="button" onClick={() => removeAdjustmentRow(idx)}
                          className="text-red-500 hover:text-red-700 text-lg font-bold px-1"
                          title="Remove">×</button>
                      </div>
                    ))}
                  </div>

                  {/* Live preview of totals */}
                  {adjustments.length > 0 && (
                    <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600 space-y-0.5">
                      {adjustments.filter(a => a.type === 'DEDUCTION' && a.amount).map((a, i) => (
                        <div key={i} className="flex justify-between text-red-600">
                          <span>− {a.label || 'Deduction'}</span>
                          <span>Rs {parseFloat(a.amount || '0').toFixed(2)}</span>
                        </div>
                      ))}
                      {adjustments.filter(a => a.type === 'ADDITION' && a.amount).map((a, i) => (
                        <div key={i} className="flex justify-between text-green-600">
                          <span>+ {a.label || 'Addition'}</span>
                          <span>Rs {parseFloat(a.amount || '0').toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Remarks */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea value={editData.remarks}
                    onChange={(e) => setEditData({ ...editData, remarks: e.target.value })}
                    rows={2}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 border p-2"
                    placeholder="Optional remarks..." />
                </div>

                {/* Live Totals Preview */}
                {(() => {
                  const liveBase = parseFloat(editData.baseSalary) || 0;
                  const liveTA = parseFloat(editData.travellingAllowance) || 0;
                  const liveComps = editCompensations.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
                  const liveAdditions = adjustments.filter(a => a.type === 'ADDITION').reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                  const liveTotalEarnings = liveBase + liveTA + liveComps + liveAdditions;
                  const liveCSG = liveBase <= 50000 ? liveBase * 0.015 : liveBase * 0.03;
                  const liveNSF = liveBase >= 29710 ? 29710 * 0.01 : liveBase * 0.01;
                  const liveTravelDed = Number(selectedPayroll.travellingDeduction) > 0
                    ? (liveTA / Number(selectedPayroll.travellingAllowance || 1)) * Number(selectedPayroll.travellingDeduction)
                    : 0;
                  const liveUserDeds = adjustments.filter(a => a.type === 'DEDUCTION').reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                  const liveManualTransfers = (selectedPayroll.transfers || [])
                    .filter(t => t.label !== 'HSA (Target Net)')
                    .reduce((s, t) => s + Number(t.amount), 0);
                  const targetNetSalary = selectedPayroll.employee.targetNetSalary;
                  const hsaTargetNet = targetNetSalary === null || targetNetSalary === undefined
                    ? 0
                    : Math.max(0, liveTotalEarnings - liveTravelDed - liveCSG - liveNSF - liveUserDeds - liveManualTransfers - Number(targetNetSalary));
                  const liveTotalDeductions = liveTravelDed + liveCSG + liveNSF + liveUserDeds + liveManualTransfers + hsaTargetNet;
                  const liveNet = liveTotalEarnings - liveTotalDeductions;
                  return (
                    <>
                      <div className="mb-4 p-3 bg-gray-900 text-white rounded-md text-sm">
                        <p className="text-xs text-gray-400 mb-2 font-semibold uppercase tracking-wide">Live Preview</p>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-300">Total Earnings</span>
                          <span className="font-semibold text-green-400">{formatCurrency(liveTotalEarnings)}</span>
                        </div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-300">Total Deductions</span>
                          <span className="font-semibold text-red-400">{formatCurrency(liveTotalDeductions)}</span>
                        </div>
                        {targetNetSalary !== null && targetNetSalary !== undefined && (
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-300">HSA Target Net</span>
                            <span className="font-semibold text-blue-300">{formatCurrency(hsaTargetNet)}</span>
                          </div>
                        )}
                        <div className="border-t border-gray-600 mt-2 pt-2 flex justify-between">
                          <span className="font-semibold">Bank Net Salary</span>
                          <span className="font-bold text-lg text-white">{formatCurrency(liveNet)}</span>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1">CSG/NSF estimated from new base salary. Changes are absorbed by HSA Target Net when a target is configured.</p>
                      </div>
                      <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md text-sm">
                        <p className="text-xs font-semibold text-orange-700 mb-2 uppercase tracking-wide">Employer Contributions (not deducted from employee)</p>
                        <div className="flex justify-between text-orange-800">
                          <span>Employer CSG ({liveBase <= 50000 ? '3%' : '6%'})</span>
                          <span>{formatCurrency(calcEmployerCSG(liveBase))}</span>
                        </div>
                        <div className="flex justify-between text-orange-800 mt-1">
                          <span>Employer NSF (2.5%)</span>
                          <span>{formatCurrency(calcEmployerNSF(liveBase))}</span>
                        </div>
                        <div className="flex justify-between text-orange-800 mt-1">
                          <span>Training Levy (1.5%)</span>
                          <span>{formatCurrency(calcTrainingLevy(liveBase))}</span>
                        </div>
                        <div className="flex justify-between text-orange-900 font-semibold mt-2 border-t border-orange-200 pt-2">
                          <span>Total Employer Contribution</span>
                          <span>{formatCurrency(calcEmployerCSG(liveBase) + calcEmployerNSF(liveBase) + calcTrainingLevy(liveBase))}</span>
                        </div>
                      </div>
                    </>
                  );
                })()}

                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => { setEditError(''); setShowModal(false); }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Payroll;
